import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Calendar, Clock, CheckCircle, AlertCircle } from 'lucide-react';
import moment from 'moment';
import { DateRangePicker as ReactDatesRangePicker } from 'react-dates';
import 'react-dates/initialize';
import 'react-dates/lib/css/_datepicker.css';

export const DateRangePicker = ({
    unitId,
    selectedCheckIn,
    selectedCheckOut,
    onDateChange,
    excludeBookingId,
    unitBookings: allUnitBookings = [],
    isLoading = false,
    error = null,
}) => {
    const { t, i18n } = useTranslation();
    const [quickSelectMode, setQuickSelectMode] = useState('manual');
    const [focusedInput, setFocusedInput] = useState(null);

    // Filter bookings by unitId and exclude the current booking
    const unitBookings = useMemo(() => {
        return allUnitBookings.filter(booking =>
            (booking.unitId === unitId || booking.unit?._id === unitId) &&
            booking.id !== excludeBookingId
        );
    }, [allUnitBookings, unitId, excludeBookingId]);

    // Generate available date ranges for the next 90 days
    const availableDateRanges = useMemo(() => {
        const today = moment();
        const endDate = moment().add(90, 'days');

        const ranges = [];
        let currentDate = moment(today);

        // Sort bookings by check-in date
        const sortedBookings = unitBookings
            .map(booking => ({
                start: moment(booking.checkIn),
                end: moment(booking.checkOut)
            }))
            .sort((a, b) => a.start.valueOf() - b.start.valueOf());

        for (const booking of sortedBookings) {
            if (currentDate.isBefore(booking.start)) {
                const rangeEnd = moment(booking.start).subtract(1, 'day');

                if (currentDate.isSameOrBefore(rangeEnd)) {
                    const nights = rangeEnd.diff(currentDate, 'days') + 1;
                    ranges.push({
                        start: currentDate.format('YYYY-MM-DD'),
                        end: rangeEnd.format('YYYY-MM-DD'),
                        nights
                    });
                }
            }
            currentDate = moment.max(currentDate, booking.end);
        }

        // Add final range if there's availability until end date
        if (currentDate.isBefore(endDate)) {
            const nights = endDate.diff(currentDate, 'days');
            ranges.push({
                start: currentDate.format('YYYY-MM-DD'),
                end: endDate.format('YYYY-MM-DD'),
                nights
            });
        }

        return ranges;
    }, [unitId, unitBookings]);

    // Generate suggested stay durations for each available range
    const suggestedStays = useMemo(() => {
        const suggestions = [];

        const commonDurations = [1, 2, 3, 7, 14, 30];

        availableDateRanges.forEach(range => {
            const rangeStart = moment(range.start);
            const rangeEnd = moment(range.end);

            commonDurations.forEach(duration => {
                const checkOut = moment(rangeStart).add(duration, 'days');

                if (checkOut.isSameOrBefore(rangeEnd)) {
                    suggestions.push({
                        checkIn: range.start,
                        checkOut: checkOut.format('YYYY-MM-DD'),
                        nights: duration,
                        label: duration === 1
                            ? t('oneNight')
                            : duration === 7
                                ? t('oneWeek')
                                : duration === 14
                                    ? t('twoWeeks')
                                    : duration === 30
                                        ? t('oneMonth')
                                        : `${duration} ${t('nights')}`
                    });
                }
            });
        });

        return suggestions.slice(0, 12); // Limit to 12 suggestions
    }, [availableDateRanges, t]);

    const isDateAvailable = (date) => {
        const targetDate = moment(date);
        return !unitBookings.some(booking => {
            const checkIn = moment(booking.checkIn);
            const checkOut = moment(booking.checkOut);
            return targetDate.isSameOrAfter(checkIn) && targetDate.isBefore(checkOut);
        });
    };

    const isValidDateRange = (checkIn, checkOut) => {
        if (!checkIn || !checkOut) return false;

        const checkInDate = moment(checkIn);
        const checkOutDate = moment(checkOut);

        if (checkOutDate.isSameOrBefore(checkInDate)) return false;

        // Check if any day in the range conflicts with existing bookings
        const currentDate = moment(checkInDate);
        while (currentDate.isBefore(checkOutDate)) {
            if (!isDateAvailable(currentDate.format('YYYY-MM-DD'))) {
                return false;
            }
            currentDate.add(1, 'day');
        }

        return true;
    };

    const calculateNights = (checkIn, checkOut) => {
        if (!checkIn || !checkOut) return 0;
        return moment(checkOut).diff(moment(checkIn), 'days');
    };

    const currentSelection = {
        isValid: isValidDateRange(selectedCheckIn, selectedCheckOut),
        nights: calculateNights(selectedCheckIn, selectedCheckOut)
    };

    // Block days that are already booked
    const isDayBlocked = (day) => {
        if (!Array.isArray(unitBookings) || unitBookings.length === 0) return false;
        const dayMoment = moment(day).startOf("day");
        for (let i = 0; i < unitBookings.length; i++) {
            const { checkIn, checkOut } = unitBookings[i];
            const fromMoment = moment(checkIn).startOf("day");
            const toMoment = moment(checkOut).startOf("day");
            // Block only if day is strictly after checkIn and strictly before checkOut
            if (dayMoment.isAfter(fromMoment) && dayMoment.isBefore(toMoment)) {
                return true;
            }
        }
        return false;
    };

    // Handle date change from ReactDatesRangePicker
    const handleDatesChange = ({ startDate, endDate }) => {
        const checkIn = startDate ? startDate.format('YYYY-MM-DD') : '';
        const checkOut = endDate ? endDate.format('YYYY-MM-DD') : '';
        onDateChange(checkIn, checkOut);
    };

    // Show loading state
    if (isLoading) {
        return (
            <div className="p-6 bg-white rounded-xl border border-slate-200">
                <div className="flex items-center justify-center space-x-2">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                    <span className="text-slate-600">{t('loading')}...</span>
                </div>
            </div>
        );
    }

    // Show error state
    if (error) {
        return (
            <div className="p-6 bg-red-50 rounded-xl border border-red-200">
                <div className="flex items-center space-x-2 text-red-600">
                    <AlertCircle className="w-5 h-5" />
                    <span>{error}</span>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4 sm:space-y-6">
            {/* Mode Toggle */}
            {/* <div className="flex rounded-lg sm:rounded-xl border border-slate-200 p-1 bg-slate-50">
                <button
                    onClick={() => setQuickSelectMode('suggestions')}
                    className={`flex-1 px-2 sm:px-4 py-1.5 sm:py-2 rounded text-xs sm:text-sm font-medium transition-colors duration-200 ${quickSelectMode === 'suggestions'
                            ? 'bg-white text-blue-600 shadow-sm'
                            : 'text-slate-600 hover:text-slate-900'
                        }`}
                >
                    {t('quickSuggestions')}
                </button>
                <button
                    onClick={() => setQuickSelectMode('manual')}
                    className={`flex-1 px-2 sm:px-4 py-1.5 sm:py-2 rounded text-xs sm:text-sm font-medium transition-colors duration-200 ${quickSelectMode === 'manual'
                            ? 'bg-white text-blue-600 shadow-sm'
                            : 'text-slate-600 hover:text-slate-900'
                        }`}
                >
                    {t('manualSelection')}
                </button>
            </div> */}

            {/* Current Selection Status */}
            {/* {(selectedCheckIn || selectedCheckOut) && (
                <div className={`p-3 sm:p-4 rounded-xl border-2 ${currentSelection.isValid
                        ? 'bg-green-50 border-green-200'
                        : 'bg-orange-50 border-orange-200'
                    }`}>
                    <div className="flex items-center space-x-3 rtl:space-x-reverse">
                        {currentSelection.isValid ? (
                            <CheckCircle size={16} className="text-green-600" />
                        ) : (
                            <AlertCircle size={16} className="text-orange-600" />
                        )}
                        <div>
                            <h4 className={`font-semibold ${currentSelection.isValid ? 'text-green-900' : 'text-orange-900'
                                } text-sm`}>
                                {currentSelection.isValid
                                    ? t('datesAvailable')
                                    : t('dateConflict')
                                }
                            </h4>
                            {selectedCheckIn && selectedCheckOut && (
                                <p className={`text-sm ${currentSelection.isValid ? 'text-green-700' : 'text-orange-700'
                                    }`}>
                                    {moment(selectedCheckIn).format('L')} - {moment(selectedCheckOut).format('L')}
                                    {currentSelection.isValid && (
                                        <span className="ms-2">
                                            ({currentSelection.nights} {currentSelection.nights === 1 ? t('night') : t('nights')})
                                        </span>
                                    )}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )} */}

            {/* Quick Suggestions */}
            {/* {quickSelectMode === 'suggestions' && (
                <div>
                    <h4 className="font-medium text-slate-900 mb-3 sm:mb-4 text-sm sm:text-base">
                        {t('suggestedStayPeriods')}
                    </h4>

                    {suggestedStays.length === 0 ? (
                        <div className="text-center py-6 sm:py-8 bg-slate-50 rounded-xl border-2 border-dashed border-slate-300">
                            <Calendar size={24} className="mx-auto text-slate-400 mb-2" />
                            <p className="text-slate-500 text-xs sm:text-sm px-4">
                                {t('noAvailableDates')}
                            </p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3">
                            {suggestedStays.map((stay, index) => (
                                <button
                                    key={index}
                                    onClick={() => onDateChange(stay.checkIn, stay.checkOut)}
                                    className={`p-2 sm:p-3 lg:p-4 rounded-lg sm:rounded-xl border-2 text-left transition-all duration-200 ${selectedCheckIn === stay.checkIn && selectedCheckOut === stay.checkOut
                                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                                            : 'border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm'
                                        }`}
                                >
                                    <div className="flex items-center justify-between mb-1 sm:mb-2">
                                        <span className="font-medium text-xs sm:text-sm">{stay.label}</span>
                                        <Clock size={12} className="text-slate-400 hidden sm:block" />
                                    </div>
                                    <div className="text-xs text-slate-600 space-y-0.5">
                                        <div>{moment(stay.checkIn).format('L')}</div>
                                        <div>{moment(stay.checkOut).format('L')}</div>
                                    </div>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            )} */}

            {/* Manual Date Selection */}
            {quickSelectMode === 'manual' && (
                <div>
                    {/* <h4 className="font-medium text-slate-900 mb-3 sm:mb-4 text-sm sm:text-base">
                        {t('manualDateSelection')}
                    </h4> */}

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                                {t("checkInDate")}
                            </label>
                            <input
                                type="date"
                                value={selectedCheckIn}
                                onChange={(e) => onDateChange(e.target.value, selectedCheckOut)}
                                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                                min={new Date().toISOString().split('T')[0]}
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-slate-700 mb-2">
                                {t("checkOutDate")}
                            </label>
                            <input
                                type="date"
                                value={selectedCheckOut}
                                onChange={(e) => onDateChange(selectedCheckIn, e.target.value)}
                                className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200"
                                min={selectedCheckIn || new Date().toISOString().split('T')[0]}
                            />
                        </div>
                    </div>

                    {/* <div className="input-calender-addRequest w-full">
                        <ReactDatesRangePicker
                            isRTL={i18n.language === "ar"}
                            startDate={selectedCheckIn ? moment(selectedCheckIn) : null}
                            endDate={selectedCheckOut ? moment(selectedCheckOut) : null}
                            onDatesChange={handleDatesChange}
                            focusedInput={focusedInput}
                            startDateId='startDateCheckAvailability'
                            endDateId='endDateCheckAvailability'
                            onFocusChange={(focusedInput) => setFocusedInput(focusedInput)}
                            startDatePlaceholderText={t("checkInDate")}
                            endDatePlaceholderText={t("checkOutDate")}
                            displayFormat="YYYY/MM/DD"
                            customArrowIcon={"-"}
                            minimumNights={1}
                            numberOfMonths={window.innerWidth > 700 ? 2 : 1}
                            withPortal
                            isDayBlocked={isDayBlocked}
                            isOutsideRange={() => false}
                            hideKeyboardShortcutsPanel
                            reopenPickerOnClearDates
                            block
                        />
                    </div> */}

                    {/* Available Ranges Info */}
                    {/* {availableDateRanges.length > 0 && (
                        <div className="mt-3 sm:mt-4">
                            <h5 className="text-xs sm:text-sm font-medium text-slate-700 mb-2">
                                {t('availablePeriods')}
                            </h5>
                            <div className="space-y-1 sm:space-y-2">
                                {availableDateRanges.slice(0, 5).map((range, index) => (
                                    <div key={index} className="flex items-center justify-between p-2 sm:p-3 bg-green-50 border border-green-200 rounded-lg">
                                        <div className="text-xs sm:text-sm">
                                            <span className="font-medium text-green-900 block sm:inline">
                                                {moment(range.start).format('L')} - {moment(range.end).format('L')}
                                            </span>
                                            <span className="text-green-700 sm:ms-2 block sm:inline">
                                                ({range.nights} {range.nights === 1 ? t('night') : t('nights')} {t('available')})
                                            </span>
                                        </div>
                                    </div>
                                ))}
                                {availableDateRanges.length > 5 && (
                                    <p className="text-xs text-slate-500 text-center px-2">
                                        {t('morePeriodsAvailable', { count: availableDateRanges.length - 5 })}
                                    </p>
                                )}
                            </div>
                        </div>
                    )} */}
                </div>
            )}
        </div>
    );
};
